import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../utils/translate';
import { useRefreshToken } from '../../common/hooks/useRefreshToken';
import { chooseInputComponent } from '../../common/utils/components';
import PropTypes from 'prop-types';
import api from '../../api';
import notify from '../Notification/helper';
import { getBeefProductOverride } from '../../utils/overrides';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value.code !== index.code}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value?.code === index?.code && <Box>{children}</Box>}
    </div>
  );
}

const BeefOverride = ({ beefCut, onComplete }) => {
  const retailer = useSelector(state => state.retailer);

  useRefreshToken();

  const languages = [
    {
      "code": "en-US",
      "name": "English"
    },
    {
      "code": "fr-FR",
      "name": "French"
    },
    {
      "code": "es-ES",
      "name": "Spanish"
    },
    {
      "code": "zh-CN",
      "name": "Chinese"
    },
    {
      "code": "tl",
      "name": "Tagalog"
    },
  ];

  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  let defaultPayload = {};
  let overrideId;

  beefCut?.beef_product_overrides?.forEach(override => {
    overrideId = override.id;

    override?.translations?.forEach(translation => {
      defaultPayload[translation.languages_code] = {
        ...translation,
        overrideId: override.id,
      };
    });
  });

  const [translationsPayload, setTranslationsPayload] = useState(defaultPayload || {});

  const [loading, setLoading] = useState(false);
  
  const submitForm = async () => {

    let createPayload = [];
    let updatePayload = [];
    let deletePayload = [];

    if (overrideId === undefined) {
      Object.keys(translationsPayload).forEach((languageCode) => {
        createPayload.push({
          status: 'published',
          retailer: retailer.id,
          translations:{
            create: [{
              languages_code: {
                code: languageCode,
              },
              name: translationsPayload[languageCode].name,
              description: translationsPayload[languageCode].description,
              specifications: translationsPayload[languageCode].specifications,
            }],
            delete: [],
            update: [],
          },
        });
      });
    } else {
      let create = [];
      let update = [];

      Object.keys(translationsPayload).forEach((languageCode) => {
        // if translation has id then update else create
        if (translationsPayload[languageCode].id === undefined) {
          create.push({
            languages_code: {
              code: languageCode,
            },
            name: translationsPayload[languageCode].name,
            description: translationsPayload[languageCode].description,
            specifications: translationsPayload[languageCode].specifications,
          });
        } else {
          update.push({
            id: translationsPayload[languageCode].id,
            languages_code: {
              code: languageCode,
            },
            name: translationsPayload[languageCode].name,
            description: translationsPayload[languageCode].description,
            specifications: translationsPayload[languageCode].specifications,
          });
        }
      });

      updatePayload.push({
        id: overrideId,
        translations: {
          create,
          update,
          delete: [],
        }
      });
    }

    let payload = {
      beef_product_overrides: {
        create: createPayload,
        update: updatePayload,
        delete: deletePayload,
      }
    };

    setLoading(true);
    
    try {
      await api.updateItem(`beef_product/${beefCut.id}`, payload);

      if (onComplete) {
        onComplete();
      }

      notify("success", "Beef Override Saved");
    } catch (err) {
      err?.map((err) => notify("error", err.message));
    } finally {
      setLoading(false);
    }
  }
  
  const handleTranslationInputChange = (languageCode, field, value) => {
    if (translationsPayload[languageCode] === undefined) {
      setTranslationsPayload({
        ...translationsPayload,
        [languageCode]: {
          [field]: value
        }
      });
    } else {
      setTranslationsPayload({
        ...translationsPayload,
        [languageCode]: {
          ...translationsPayload[languageCode],
          [field]: value
        }
      });
    }
  };

  const formSections = [
    {
      title: 'Override Beef Cut',
      translations: [
        {
          label: 'Name',
          name: 'name',
          description: 'The name that will override the default beef cut name.',
          type: 'text',
          value: translationsPayload,
          options: [],
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        },
        {
          label: 'Description',
          name: 'description',
          description: 'The description that will override the default beef cut description.',
          type: 'rich-text',
          value: translationsPayload,
          options: [],
          config: {
            fieldType: 'textarea',
          },
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        },
        {
          label: 'Specifications',
          name: 'specifications',
          description: 'A detailed description of the particulars of a beef cut that will be used in wholesale gateways.',
          type: 'rich-text',
          value: translationsPayload,
          options: [],
          config: {
            fieldType: 'textarea',
          },
          handleInputChange: (field, value, languageCode) => {
            handleTranslationInputChange(languageCode, field, value);
          }
        },
      ]
    },
  ]
  
  return (
    <Box>
      <Grid container>
        <Grid item xs={12} lg={7}>
          {formSections.map((section) => (
            <Box key={section.title}>
              {
                section.title &&
                <Typography
                  className='form-section-title'
                  variant="h4"
                >
                  {section.title}
                </Typography>
              }

              <Box>
                <Stack
                  direction={'row'}
                  spacing={1}
                  alignItems={'center'}
                  sx={{
                    background: '#efeeee',
                    p: 0.5,
                  }}
                >
                  <Typography
                    component={'div'}
                    sx={{
                      flexGrow: 1,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      px: 1,
                    }}
                  >
                    Edit in <strong>{ selectedLanguage.name }</strong>
                  </Typography>

                  {
                    languages.map((languageOption) => (
                      <Box
                        key={languageOption.code}
                        sx={{
                          position: 'relative',
                          height: '32px',
                          width: '28px',
                          cursor: 'pointer',
                        }}
                        onClick={() => { setSelectedLanguage(languageOption); }}
                      >
                        <Stack
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                          className=''
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                            background: '#fff',
                            borderBottom: (
                              selectedLanguage.code === languageOption.code ?
                              `4px solid ${retailer.button_background_color || 'red'}` :
                              '4px solid #efeeee'
                            ),
                            borderTop: (
                              (!translationsPayload?.[languageOption.code]?.['name'] ? '2px solid #efeeee' : '2px solid green')
                            ),
                            borderRadius: '0'
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'black',
                              fontSize: 16,
                              textTransform: 'uppercase',
                              fontWeight: selectedLanguage.code === languageOption.code ? 'bold' : 'normal',
          
                            }}
                          >
                            {languageOption.code.substring(0, 2)}
                          </Typography>
                        </Stack>
                      </Box>
                    ))
                  }
                </Stack>

                <Box>
                  {
                    languages.map((language) => (
                      <CustomTabPanel
                        key={language.name}
                        value={selectedLanguage}
                        index={language}
                      >
                        <Grid container spacing={2}>
                          {section?.translations?.map(field => (
                            <Fragment key={field.name}>
                              <Grid
                                key={field.name}
                                item
                                {
                                ...(['colour'].includes(field?.type) ? {
                                  xs: 12,
                                  md: 4,
                                } : {
                                  xs: 12,
                                }
                                )
                                }
                              >
                                {chooseInputComponent(field, language.code)}
                              </Grid>
                            </Fragment>
                          ))}
                        </Grid>
                      </CustomTabPanel>
                    ))
                  }
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>

        <Grid item xs={12} lg={7} >
          <Button
            disabled={loading}
            className='form-button'
            sx={{ width: '40%', my: 5 }}
            size="large"
            variant="contained"
            onClick={submitForm}
          >
            {loading ? 'Saving... ' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BeefOverride;
